import { Alert, Button, Col, Dropdown, Popover, Row, Space, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { useAppSelector } from '../../app/hooks';
import { ASCII_UPPERCASE, defaultPlotlyArguments, getPeptideSetType, spots2peptides } from '../../compute/utils';
import { selectColormap, selectCurrentRecordID, selectRecordData, selectSessionID } from '../../features/analysisConfig/analysisConfigSlice';
import { Contrasts, PeptideSet, ContrastsFigureType } from '../../types/analysisTypes';
import { fetchAuthorizedAPIEndpoint, useAuth0OrQueryAuth } from '../../utils';
import { ArrowsAltOutlined, DownOutlined } from '@ant-design/icons';
import { DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, nonStandardPeptidesColor, peptideColorPaletteVdW } from '../../compute/colormap';
import FullscreenGraphicModal from './FullscreenGraphicModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { arycolor } from '../../assets/css/color';
import { getContrastThresholdFromType } from '../../compute/contrast';

export const ContrastPanel: React.FC<{}> = () => {
  const accessToken = useAuth0OrQueryAuth();

  const sessionID = useAppSelector(selectSessionID);
  const currentRecordID = useAppSelector(selectCurrentRecordID);
  const record = useAppSelector(selectRecordData);

  const [contrasts, setContrasts] = useState<Contrasts | null>(null);
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [peptidesSetType, setPeptidesSetType] = useState<PeptideSet>(PeptideSet.Unknown);
  const [figureType, setFigureType] = useState<ContrastsFigureType>(ContrastsFigureType.Spatial);

  const contrastThreshold = contrasts ? getContrastThresholdFromType(contrasts.Type) : 0;

  useEffect(() => {
    if (!accessToken) return;
    if (sessionID !== '') {
      fetchAuthorizedAPIEndpoint(`/compute/mmi/constrast?session_id=${sessionID}&record_id=${currentRecordID}`, accessToken)
        .then((resp) => {
          if (resp.ok) {
            return resp.json();
          } else {
            throw new Error();
          }
        })
        .then((x: Contrasts) => {
          if (x !== null) {
            setContrasts(x);
          }
        });
    }
  }, [accessToken, sessionID, currentRecordID]);

  useEffect(() => {
    if (record && record.Sensors !== null) {
      console.log('record is', record);
      const spotgrid = [...record.Sensors.map((s) => parseInt(s))]; // parseInt tranform peptides with coordonate to number ex: 134[A3] = 134
      setPeptidesSetType(getPeptideSetType(spotgrid, true));
    } else setPeptidesSetType(PeptideSet.Unknown);
  }, [record]);

  if (error !== '') {
    return (
      <div>
        <Alert type="error" message={error} />
      </div>
    );
  }

  if (contrasts === null) {
    return null;
  }

  var plotlyData: Plotly.Data[] = [];
  var plotlyLayout: Partial<Plotly.Layout> = { ...defaultPlotlyArguments.layout, paper_bgcolor: '#f8f8f8' };

  switch (figureType) {
    case +ContrastsFigureType.Bar:
      plotlyData = [
        {
          type: 'bar',
          x: contrasts.Sensors.map((p) => p), // A hack so that plotly treats peptide codes as categories (not degrees)
          y: contrasts.Values,
          marker: {
            color: contrasts.Sensors.map((spotFullStr) => {
              let peptideCodeInt = parseInt(spotFullStr);
              let color: string = DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE;
              if (peptidesSetType === PeptideSet.POR1 || peptidesSetType === PeptideSet.POR2 || peptidesSetType === PeptideSet.POR3 || peptidesSetType === PeptideSet.POR4) color = peptideColorPaletteVdW[peptideCodeInt];
              else if (peptidesSetType === PeptideSet.NonStandard) color = nonStandardPeptidesColor[peptideCodeInt % nonStandardPeptidesColor.length];

              if (color === undefined) color = DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE;

              return color;
            }),
          },
          hoverinfo: 'y+x',
          hovertemplate: '%{x}: %{y:.1f}%<extra></extra>',
          hoveron: 'points',
        },
      ];
      plotlyLayout.xaxis = {
        automargin: true,
        type: 'category',
      };
      plotlyLayout.yaxis = {
        title: 'Optical contrast (%)',
      };
      plotlyLayout.shapes = [
        {
          type: 'line',
          x0: 0,
          x1: 1,
          xref: 'paper', // span full width
          y0: contrastThreshold,
          y1: contrastThreshold,
          yref: 'y',
          line: {
            color: 'red',
            width: 2,
            dash: 'dash', // dashed style
          },
        },
      ];
      break;
    case +ContrastsFigureType.Spatial:
      try {
        let [sensorNature, sensorCoordinateInts] = spots2peptides(contrasts.Sensors);
        let rowNb = Math.max(...sensorCoordinateInts.map((e) => e[0]));
        let colNb = Math.max(...sensorCoordinateInts.map((e) => e[1]));

        let X: number[] = [];
        let Y: number[] = [];
        let Z: number[][] = [];
        let intCoords: [number, number][] = [];

        for (let colIdx = 0; colIdx < colNb + 1; colIdx++) {
          X.push(colIdx);
        }
        for (let rowIdx = 0; rowIdx < rowNb + 1; rowIdx++) {
          Y.push(rowIdx);
        }

        for (let rowIdx = 0; rowIdx < rowNb + 1; rowIdx++) {
          let row: number[] = [];
          for (let colIdx = 0; colIdx < colNb + 1; colIdx++) {
            row.push(NaN);
          }
          Z.push(row);
        }
        sensorCoordinateInts.forEach(([row, col], i) => {
          Z[row][col] = contrasts.Values[i];
        });
        plotlyData = [
          {
            type: 'heatmap',
            x: X.map((v) => `${v}`),
            y: Y.map((v) => `${ASCII_UPPERCASE[v]}`),
            z: Z,
            hovertemplate: '%{y}%{x}: %{z:.1f}%<extra></extra>',
            hoveron: 'points',
            colorscale: [
              [0, 'rgb(165,0,38)'],
              [0.035, 'rgb(210, 98, 18)'],
              [0.07, 'rgb(244, 220, 67)'],
              [0.105, 'rgb(131, 253, 97)'],
              [0.14, 'rgb(3, 217, 106)'],
              [1, 'rgb(5, 188, 94)'],
            ],
            showscale: false,
            zmin: 0,
            zmax: 100,
          },
        ];
        plotlyLayout.yaxis = {
          //   autorange: 'reversed',
          range: [15.5, -0.5],
          ticks: '',
        };
        plotlyLayout.xaxis = {
          //   autorange: 'reversed',
          range: [-0.5, 3.5],
          ticks: '',
          type: 'category',
          side: 'top',
        };
        plotlyLayout.annotations = sensorCoordinateInts.map(([row, col], i) => {
          return {
            x: col,
            y: row,
            text: sensorNature[i],
            showarrow: false,
            font: {
              color: 'white',
              family: 'century gothic',
            },
          };
        });
      } catch (e) {
        console.log(e);
        return null;
      }
      break;
  }
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      <h3 style={{ margin: 'auto', marginBottom: 10, textAlign: 'center' }}>Sensor Integrity Control</h3>
      <Row justify="space-between" align="middle">
        <Col></Col>
        <Col>
          <Space align="center" style={{ display: 'flex', justifyContent: 'center' }}>
            <Dropdown
              menu={{
                items: Object.entries(ContrastsFigureType)
                  .filter(([_, value]) => typeof value === 'number')
                  .map(([label, value]) => {
                    return {
                      key: +value,
                      label: label,
                      onClick: () => {
                        setFigureType(+value);
                      },
                    };
                  }),
              }}
            >
              <Tooltip title="Type">
                <Button style={{ borderRadius: '5px' }}>
                  <Space>
                    {ContrastsFigureType[figureType]}
                    <DownOutlined />
                  </Space>
                </Button>
              </Tooltip>
            </Dropdown>
            <Popover
              content={
                <>
                  <b>Mean Optical Contrast:</b> {(contrasts.Values.reduce((a, b) => a + b, 0) / contrasts.Values.length).toFixed(1)}%
                  <br />
                  <b>Number of filtered spots:</b> {contrasts.Values.reduce((a, b) => (b < contrastThreshold ? a + 1 : a), 0)}
                  <br />
                  <b>Date:</b> {new Date((record?.AbsoluteTimestamp || 0) * 1000).toLocaleString()}
                </>
              }
            >
              <FontAwesomeIcon
                icon="info-circle"
                className="clickable-icon"
                style={{
                  color: arycolor.aryBlue,
                }}
              />
            </Popover>
          </Space>
        </Col>
        <ArrowsAltOutlined className="clickable-icon" onClick={() => setIsVisibleModal(true)} />
      </Row>
      <Plot divId="contrasts_plot" debug={true} data={plotlyData} layout={plotlyLayout} useResizeHandler={true} config={defaultPlotlyArguments.config} style={defaultPlotlyArguments.style} />
      <FullscreenGraphicModal title="Sensor Integrity Control" visible={isVisibleModal} onCancel={() => setIsVisibleModal(false)}>
        <Plot divId="contrasts_plot" debug={true} data={plotlyData} layout={plotlyLayout} useResizeHandler={true} config={defaultPlotlyArguments.config} style={defaultPlotlyArguments.style} />
      </FullscreenGraphicModal>
    </div>
  );
};
